"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"

import { Focus, Sun, SunDim } from "lucide-react"

import { cn } from "@/lib/utils"
import { analysisFunnelEnvelope, runWhenPageVisible, trackEvent } from "@/lib/analytics"
import { photoQualityAnalyticsProps } from "@/lib/analysis/photo-quality-analytics"
import type { PhotoQualityReason } from "@/lib/analysis/photo-quality"
import type { PhotoQualityWarning as PhotoQualityWarningValue } from "@/hooks/use-homepage-analysis"

type WarningMode = "identify" | "diagnose"

interface PhotoQualityWarningProps {
  warning: PhotoQualityWarningValue | null
  mode: WarningMode
  funnelId?: string | null
  authState?: "anonymous" | "authenticated" | "unknown"
  onRetake: () => void
  className?: string
}

/** The icon encodes the fix, not the defect, so the remedy is recognized before
 *  the sentence is read. */
const ICONS = {
  blur: Focus,
  dark: Sun,
  bright: SunDim,
} as const

const HEADINGS: Record<PhotoQualityReason, string> = {
  blur: "This photo looks blurry",
  dark: "This photo looks dark",
  bright: "This photo looks washed out",
}

const BODY: Record<WarningMode, Record<PhotoQualityReason, string>> = {
  identify: {
    blur: "A sharper shot gets you a confident match.",
    dark: "Blade color drives the match, so daylight helps a lot.",
    bright: "Shade keeps the blade color true.",
  },
  diagnose: {
    blur: "A sharper shot makes the problem edge readable.",
    dark: "Daylight shows the real color of the damage.",
    bright: "Softer light keeps the damaged area's color true.",
  },
}

/**
 * Impression dedup keyed `attemptId:reason:role`. Module scope on purpose: a
 * retryable analysis failure unmounts and remounts this component on the way
 * back to the ZIP step, and a component ref would re-fire the shown event for
 * the same attempt. Attempt ids are unique per upload, so this only ever
 * suppresses true duplicates within the page lifetime.
 */
const shownImpressionKeys = new Set<string>()

/** Test-only: impression dedup is module state, so suites must reset it. */
export function resetPhotoQualityImpressionsForTests() {
  shownImpressionKeys.clear()
}

/**
 * Soft, pre-call capture warning. Advisory only: the analyze button below it
 * stays enabled and is itself the override, so this card carries exactly one
 * action. Amber rather than red — red already means "this failed" on this
 * surface, and this means "this will probably disappoint you".
 */
export function PhotoQualityWarning({
  warning,
  mode,
  funnelId,
  authState = "unknown",
  onRetake,
  className,
}: PhotoQualityWarningProps) {
  const pathname = usePathname()

  const reason = warning?.reason ?? null
  const attemptId = warning?.attemptId ?? null
  const role = warning?.role ?? null

  useEffect(() => {
    if (!warning || !reason || !role) return
    const key = `${attemptId}:${reason}:${role}`
    if (shownImpressionKeys.has(key)) return
    shownImpressionKeys.add(key)

    return runWhenPageVisible(() => {
      trackEvent("capture_quality_warning_shown", {
        ...analysisFunnelEnvelope({
          funnelId,
          attemptId,
          mode,
          source: "unknown",
          imageRoles: [role],
          authState,
          actualDestination: "zip_step",
        }),
        attempt_id: attemptId ?? undefined,
        ...photoQualityAnalyticsProps(warning),
        page_path: pathname ?? undefined,
      })
    })
    // `warning` is a stable object per measurement; the key set guards re-fires.
  }, [warning, reason, role, attemptId, funnelId, mode, authState, pathname])

  if (!warning || !reason) return null

  const Icon = ICONS[reason]

  return (
    <div
      role="status"
      data-testid="photo-quality-warning"
      className={cn(
        "mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-left",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-amber-200 bg-white text-amber-600">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">{HEADINGS[reason]}</p>
          <p className="mt-0.5 text-[13px] leading-snug text-amber-800/90">
            {BODY[mode][reason]}
          </p>
          <button
            type="button"
            onClick={() => {
              trackEvent("capture_quality_retake", {
                ...analysisFunnelEnvelope({
                  funnelId,
                  attemptId,
                  mode,
                  source: "unknown",
                  imageRoles: [role ?? "primary"],
                  authState,
                  actualDestination: "zip_step",
                }),
                attempt_id: attemptId ?? undefined,
                ...photoQualityAnalyticsProps(warning),
                page_path: pathname ?? undefined,
              })
              onRetake()
            }}
            className="mt-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-50 focus:outline-hidden focus:ring-2 focus:ring-amber-500/30"
          >
            Take a new photo
          </button>
        </div>
      </div>
    </div>
  )
}
